//  0 - import required functions and variables
import React, { createContext, useEffect, useState } from 'react'
import server from '../server'
import {
  normalizeTableRows,
  ALL_TABLE_ROWS_SAVE_OPTIONS,
} from '../shared/tablePagination'

//  1 - create context
export const AuthenticationContext = createContext()

// export const useAuth = () => {
//   return useContext(AuthenticationContext);
// };

//  2 - create provider
export const AuthenticationContextProvider = ({ children }) => {
  //  2.0 - grap required states, states handlers and functions from other providers

  //  2.1 - define our states
  const [isLoading, setIsLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [accessToken, setAccessToken] = useState(null)
  const [user, setUser] = useState(null)
  const [status, setStatus] = useState(null)
  const [bannerMsg, setBannerMsg] = useState(null)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [isAdmin, setIsAdmin] = useState(null)
  const [toastStatus, setToastStatus] = useState({})
  const [collapsed, setCollapsed] = useState(false)
  const [module, setModule] = useState('')

  const getErrorMessage = (err) =>
    err?.response?.data?.message || err?.message || 'Something went wrong'

  useEffect(() => {
    const storedIsAdmin = localStorage.getItem('isAdmin')
    if (storedIsAdmin !== null) {
      setIsAdmin(storedIsAdmin === 'true') // Convert string 'true'/'false' to boolean
    }
  }, [])

  let catchErr = (err) => {
    setToastStatus({
      toastStatus: 'error',
      msg: getErrorMessage(err),
    })
  }

  useEffect(() => {
    if (Object.keys(toastStatus).length !== 0) {
      setTimeout(() => {
        setToastStatus({})
      }, 3000)
    }

    if (user)
      localStorage.setItem(
        'user',
        typeof user === 'object' ? JSON.stringify(user) : null
      )
    if (status) localStorage.setItem('status', status.toString())

    if (bannerMsg) localStorage.setItem('bannerMsg', bannerMsg)
  }, [toastStatus, status, user, bannerMsg])

  const onLogin = async (email, password, role, accountNo, accessCode, domain) => {
    setIsLoading(true)

    const requestPayload = {
      email,
      password,
      role,
      ...(accessCode && { accessCode }),
      ...(domain && { domain }),
    }

    server
      .post('/users/signin', requestPayload)
      .then((response) => {
        const token = response.data.accessToken
        setStatus(response?.data?.userBanner?.status)
        setBannerMsg(response?.data?.userBanner?.message)
        // get user data
        server
          .get('/users', { headers: { Authorization: `Bearer ${token}` } })
          .then((response) => {
            const userRole = response.data.user.role
            const isAdminFlag = userRole === 'admin' ? true : false

            setUser(response.data.user)

            setError(null)
            setIsLoading(false)
            // Only set isAdmin if it's not already stored
            if (localStorage.getItem('isAdmin') === null) {
              setIsAdmin(isAdminFlag)
              localStorage.setItem('isAdmin', isAdminFlag) // Store isAdmin once
            } else {
            }
          })
        // save access token
        setAccessToken(response.data.accessToken)
        localStorage.setItem('accessToken', response.data.accessToken)
      })
      .catch((error) => {
        const msg = getErrorMessage(error)
        setToastStatus({
          toastStatus: 'error',
          msg,
        })
        setError(msg)
        setIsLoading(false)
      })
  }

  //      2.2.1.2 - handle register
  const onRegister = (email, pharmacyname, selectedAddress, agreement) => {
    server
      .post('/users/signup', {
        email: email,
        name: pharmacyname,
        address: selectedAddress,
        agreement: agreement,
      })
      .then((response) => {
        setError(null)
        setToastStatus({
          toastStatus: 'success',
          summary: 'Email Sent',
          msg: 'An email has been sent, please verify your account.',
        })
        setIsLoading(false)
      })
      .catch((error) => {
        const msg = getErrorMessage(error)
        setError(msg)
        setToastStatus({
          toastStatus: 'error',
          msg,
        })
        setIsLoading(false)
      })
  }

  const onVerify = (id, password, repeatedPassword) => {
    setIsLoading(true)

    if (password !== repeatedPassword) {
      setError('The passwords you entered do not match')
      return
    }
    if (password === '') {
      setError('You must enter a password')
      return
    }

    server
      .post('/users/verify-email', {
        uuid: id,
        password: password,
      })
      .then((response) => {
        const token = response.data.accessToken
        // get user data
        server
          .get('/users', { headers: { Authorization: `Bearer ${token}` } })
          .then((response) => {
            const createdUser = response.data?.user
            const pharmacyUser = response.data?.userBanner

            setIsAdmin(response.data.user.role === 'admin' ? true : false)
            setUser(createdUser)
            // Checks if user has a banner and sets its values
            if (createdUser.role === 'pharmacy' && pharmacyUser) {
              setStatus(pharmacyUser.status)
              setBannerMsg(pharmacyUser.message)
            }

            setError(null)
            setIsLoading(false)
          })
        // save access token
        setAccessToken(response.data.accessToken)
        localStorage.setItem('accessToken', response.data.accessToken)
      })
      .catch((error) => {
        setError(getErrorMessage(error))
        setIsLoading(false)
      })
  }

  //      2.2.1.3 - handle send forgot password email | Post request to send email for forget password usage
  const onSendForgetPasswordEmail = (email) => {
    setIsLoading(true)

    server
      .post('/users/send-forget-password-email', {
        email,
      })
      .then((response) => {
        setToastStatus({
          toastStatus: 'success',
          msg: response.data.message,
        })
        setIsLoading(false)
      })
      .catch((error) => {
        const msg = getErrorMessage(error)
        setError(msg)
        setToastStatus({
          toastStatus: 'error',
          msg,
        })

        setIsLoading(false)
      })
    setIsLoading(false)
  }

  //      2.2.1.4 - handle change password with old password | Patch request to change password on user profile
  const onChangePasswordProfile = (
    oldPassword,
    newPassword,
    confirmPassword
  ) => {
    setIsLoading(true)

    if (newPassword !== confirmPassword) {
      setToastStatus({
        toastStatus: 'error',
        msg: 'Password & Repeat Password fields do not match',
      })
      setIsLoading(false)
      return false
    }

    if (oldPassword === confirmPassword) {
      setToastStatus({
        toastStatus: 'error',
        msg: 'Please enter a new password ',
      })
      setIsLoading(false)
      return false
    }

    server
      .patch('/users/change-password', {
        oldPassword: oldPassword,
        newPassword: newPassword,
      })
      .then((response) => {
        setToastStatus({
          toastStatus: 'success',
          msg: response.data.message,
        })
      })
      .catch((error) => {
        const msg = getErrorMessage(error)
        setError(msg)
        setToastStatus({
          toastStatus: 'error',
          msg,
        })

        setIsLoading(false)
      })
    setIsLoading(false)
  }

  //      2.2.1.5 - handle change password using access token | Patch request to change password using access token
  const onChangePassword = (accessToken, password, repeatedPassword) => {
    if (password !== repeatedPassword) {
      setToastStatus({
        toastStatus: 'error',
        msg: 'The passwords you entered do not match',
      })
      return
    }
    if (password === '') {
      setToastStatus({
        toastStatus: 'error',
        msg: 'You must enter a password',
      })
      return
    }

    setIsLoading(true)
    server
      .patch('/users/change-password-link', {
        token: accessToken,
        newPassword: password,
      })
      .then((response) => {
        setToastStatus({
          toastStatus: 'success',
          msg: response.data.message,
        })
        setIsLoading(false)
        // Clear any existing session
        localStorage.removeItem('accessToken')
        localStorage.removeItem('user')
        localStorage.removeItem('isAdmin')
        setUser(null)
        // Redirect to login after successful password reset
        setTimeout(() => {
          window.location.href = '/login'
        }, 2000)
      })
      .catch((error) => {
        setToastStatus({
          toastStatus: 'error',
          msg: error.response?.data?.message || 'Failed to reset password',
        })
        setIsLoading(false)
      })
  }

  //      2.2.1.6 - get user data
  const onGetUserData = () => {
    setLoading(true)
    server
      .get('/users')
      .then((response) => {
        setIsAdmin(response.data.user.role === 'admin' ? true : false)
        setUser(response.data.user)
        setError(null)
        setLoading(false)
      })
      .catch((error) => {
        setLoading(false)
      })
  }

  const onProfileUpdate = (
    displayName,
    legalName,
    tradeName,
    editAccreditationNo,
    address,
    telephone,
    fax,
    shippingAddress,
    pharmacyOwnerName,
    pharmacyOwnerTelephone,
    pharmacyOwnerEmail,
    id,
    pharmacySystems,
    status,
    pharmacyGroupId
  ) => {
    let requestBody = {}
    setSuccess(false)
    console.log('pharmacySystems111', pharmacySystems)
    if (user?.role === 'admin') {
      if (id) {
        if (displayName) {
          requestBody.displayName = displayName
        }
        if (legalName) {
          requestBody.legalName = legalName
        }
        if (tradeName) {
          requestBody.tradeName = tradeName
        }
        if (editAccreditationNo) {
          requestBody.accreditation = editAccreditationNo
        }
        if (address) {
          requestBody.address = address
        }
        if (telephone) {
          requestBody.telephone = telephone
        }
        if (fax) {
          requestBody.fax = fax
        }
        if (shippingAddress) {
          requestBody.shippingAddress = shippingAddress
        }
        if (pharmacyOwnerName) {
          requestBody.pharmacyOwnerName = pharmacyOwnerName
        }
        if (pharmacyOwnerEmail !== undefined) {
          requestBody.pharmacyOwnerEmail =
            pharmacyOwnerEmail === '' ? null : pharmacyOwnerEmail
        }
        if (pharmacyOwnerTelephone !== undefined) {
          requestBody.pharmacyOwnerTelephone =
            pharmacyOwnerTelephone === '' ? null : pharmacyOwnerTelephone
        }
        if (pharmacySystems) {
          requestBody.pharmacySystems = pharmacySystems
        }
        if (status) {
          requestBody.status = status
        }
        if (pharmacyGroupId !== undefined) {
          requestBody.pharmacyGroupId = pharmacyGroupId === '' ? null : pharmacyGroupId
        }
      } else {
        if (displayName) {
          requestBody.displayName = displayName
        }
      }
    } else {
      if (displayName) {
        requestBody.displayName = displayName
      }

      if (legalName) {
        requestBody.legalName = legalName
      }
      if (tradeName) {
        requestBody.tradeName = tradeName
      }
      if (editAccreditationNo) {
        requestBody.accreditation = editAccreditationNo
      }
      if (address) {
        requestBody.address = address
      }
      if (telephone) {
        requestBody.telephone = telephone
      }
      if (fax) {
        requestBody.fax = fax
      }
      if (shippingAddress) {
        requestBody.shippingAddress = shippingAddress
      }
      if (pharmacyOwnerName) {
        requestBody.pharmacyOwnerName = pharmacyOwnerName
      }
      if (pharmacyOwnerEmail !== undefined) {
        requestBody.pharmacyOwnerEmail =
          pharmacyOwnerEmail === '' ? null : pharmacyOwnerEmail
      }
      if (pharmacyOwnerTelephone !== undefined) {
        requestBody.pharmacyOwnerTelephone =
          pharmacyOwnerTelephone === '' ? null : pharmacyOwnerTelephone
      }
    }

    if (isAdmin !== undefined) {
      server
        .patch(
          user?.role === 'admin' && id ? `/users/${id}` : `/users`,
          requestBody,
          {
          }
        )
        .then((response) => {
          setSuccess(true)

          if (user?.role !== 'admin') {
            setStatus(response?.data?.userBanner?.status)
            setBannerMsg(response?.data?.userBanner?.message)
          }

          setToastStatus({
            toastStatus: 'success',
            msg: response.data.message,
          })

          onGetUserData()
        })
        .catch((error) => {
          console.error('Error updating profile:', error)
          setError(error)
          setToastStatus({
            toastStatus: 'error',
            msg: 'Error updating profile',
          })
        })
    } else {
      console.error('isAdmin is not defined')
    }
  }

  const saveTableRowsPerPage = (rows) => {
    const tableRowsPerPage = normalizeTableRows(rows, ALL_TABLE_ROWS_SAVE_OPTIONS)
    if (!localStorage.getItem('accessToken')) return Promise.resolve()

    return server
      .patch(
        '/users',
        { tableRowsPerPage },
        {
        }
      )
      .then(() => {
        setUser((prev) =>
          prev ? { ...prev, tableRowsPerPage } : prev
        )
      })
      .catch((error) => {
        console.error('Error saving table rows per page preference:', error)
      })
  }

  //      2.2.1.8 - handle logout
  const onLogout = () => {
    // Save the preferences we want to keep
    const showSystemModal = localStorage.getItem('showSystemModal')
    const lastLoginEmail = localStorage.getItem('lastLoginEmail')

    // Clear everything else from localStorage. The cart is no longer preserved
    // here: the draft order lives server-side and re-hydrates on the next login
    // (on any device), so we intentionally drop the local `myOrder` cache to
    // avoid leaking one user's cart into the next session on a shared PC.
    localStorage.clear()

    // Restore the preferences we saved
    if (showSystemModal !== null) {
      localStorage.setItem('showSystemModal', showSystemModal)
    }

    if (lastLoginEmail !== null) {
      localStorage.setItem('lastLoginEmail', lastLoginEmail)
    }

    setUser(null)
    setStatus(null)
    setBannerMsg(null)
    setAccessToken(null)
    setIsAdmin(null)
  }

  const onUpdatePaymentTransaction = (id, paymentData) => {
    setLoading(true)

    const formattedData = {
      paymentStatus: paymentData.paymentStatus,
      paPaymentTransactionId: paymentData.transactionId,
      paPaymentDate: paymentData.paymentDate
        ? paymentData.paymentDate.toISOString()
        : null,
    }

    return server
      .patch(`/admins/payables/${id}`, formattedData)
      .then((response) => {
        setLoading(false)
        setSuccess(true)

        // Reset success state after a short delay
        setTimeout(() => {
          setSuccess(false)
        }, 100)

        // Update toast status for feedback
        setToastStatus({
          toastStatus: 'success',
          summary: 'Update Successful',
          msg: 'Payment status updated successfully',
        })

        // Return the data for possible further handling
        return response.data.data
      })
      .catch((error) => {
        setLoading(false)

        // Set error toast with improved error handling
        setToastStatus({
          toastStatus: 'error',
          summary: 'Update Failed',
          msg:
            error.response?.data?.message ||
            error.message ||
            'Failed to update payment status',
        })

        // Rethrow to allow for additional handling
        throw error
      })
  }

  // 2.3 - return needed states, states handlers and functions in you provider
  return (
    <AuthenticationContext.Provider
      value={{
        user,
        status,
        error,
        bannerMsg,
        success,
        isAdmin,
        isLoading,
        loading,
        accessToken,
        toastStatus,
        collapsed,
        module,
        setCollapsed,
        setIsAdmin,
        setBannerMsg,
        setStatus,
        setError,
        setUser,
        setModule,
        setIsLoading,
        setToastStatus,
        onLogin,
        onVerify,
        onChangePassword,
        onChangePasswordProfile,
        onSendForgetPasswordEmail,
        onGetUserData,
        onProfileUpdate,
        onRegister,
        onLogout,
        onUpdatePaymentTransaction,
        saveTableRowsPerPage,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  )
}
